  <!--This is  About Template   -->
<template>
  <section class="px-6 md:px-12 py-16">
    <div class="max-w-5xl mx-auto">
      <Motion
        tag="div"
        :initial="{ opacity: 0, y: 50 }"
        :animate="{ opacity: 1, y: 0 }"
        :transition="{ duration: 0.8 }"
      >
        <h2 class="text-3xl md:text-4xl font-bold mb-8">
          About <span class="text-accent">Rob</span>
        </h2>
        <div class="w-20 h-0.5 bg-accent mb-12"></div>
        <!-- Main introduction -->
        <div class="grid md:grid-cols-2 gap-12 items-center mb-16" @click.stop>
          <Motion
            tag="div"
            class="space-y-6"
            :initial="{ opacity: 0, x: -30 }"
            :animate="{ opacity: 1, x: 0 }"
            :transition="{ duration: 0.8, delay: 0.2 }"
          >
            <p class="text-lg leading-relaxed text-muted-foreground">
              Rob Rickard is a US 'techpreneur' residing in Kigali, Rwanda. Rob
              is a veteran technology entrepreneur and product strategist known
              for building high-impact systems and teams. For over three
              decades, he has led innovation at the intersection of software,
              design, and human systems. He has been a sought-after
              thought-leader & public speaker on technology, communication, and
              productivity. In addition, Rob is actively mentoring and leading
              tech startups and collaborating with other tech leaders in Kigali
              when he is not spending time with his wonderful wife, Jennifer.
            </p>

            <Motion
              tag="div"
              class="flex space-x-4"
              :initial="{ opacity: 0, y: 20 }"
              :animate="{ opacity: 1, y: 0 }"
              :transition="{ duration: 0.8, delay: 0.4 }"
            >
              <a
                href="https://x.com/Rickard"
                target="_blank"
                rel="noopener noreferrer"
                class="flex items-center justify-center w-12 h-12 bg-secondary/50 rounded-lg hover:bg-accent/20 transition-all duration-300"
              >
                <!-- Twitter Icon -->
                <Twitter
                  class="w-5 h-5 text-muted-foreground hover:text-accent transition-colors"
                />
              </a>
              <a
                href="https://www.linkedin.com/in/robrickard/"
                target="_blank"
                rel="noopener noreferrer"
                class="flex items-center justify-center w-12 h-12 bg-secondary/50 rounded-lg hover:bg-accent/20 transition-all duration-300"
              >
                <!-- LinkedIn Icon -->
                <Linkedin
                  class="w-5 h-5 text-muted-foreground hover:text-accent transition-colors"
                />
              </a>
              <a
                @click="() => onNavigate('cv')"
                rel="noopener noreferrer"
                class="cursor-pointer inline-flex items-center space-x-2 bg-accent hover:bg-accent/80 text-background px-6 py-3 rounded-lg transition-all duration-300 group aos-init aos-animate"
                data-aos="fade-up"
                data-aos-delay="800"
                ><span class="font-medium">More About Rob</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="lucide lucide-external-link-icon lucide-external-link w-4 h-4 group-hover:translate-x-1 transition-transform duration-300"
                >
                  <path d="M15 3h6v6"></path>
                  <path d="M10 14 21 3"></path>
                  <path
                    d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"
                  ></path></svg
              ></a>
            </Motion>
          </Motion>
          <Motion
            tag="div"
            class="space-y-8"
            :initial="{ opacity: 0, x: 30 }"
            :animate="{ opacity: 1, x: 0 }"
            :transition="{ duration: 0.8, delay: 0.4 }"
          >
            <div class="rounded-lg overflow-hidden">
              <!-- Launch Hello Kigali Image -->
              <img
                :src="kigaliSoftwareImage"
                alt="Rob Rickard at Celebration of Kigali Software event"
                className="w-full h-auto object-cover"
              />
            </div>
          </Motion>
        </div>
        <!-- Quote -->
        <div class="grid md:grid-cols-2 gap-12 items-center mb-16">
          <Motion
            tag="div"
            class="space-y-6"
            :initial="{ opacity: 0, x: -30 }"
            :animate="{ opacity: 1, x: 0 }"
            :transition="{ duration: 0.8, delay: 0.2 }"
          >
            <blockquote
              class="border-l-4 border-accent pl-6 py-4 bg-secondary/30 rounded-r-lg"
            >
              <p
                class="text-lg italic leading-relaxed text-muted-foreground mb-4"
              >
                "I feel so lucky to be here in Africa at this time in history.
                To witness and be part of the African tech revolution... I want
                to push Kigali to become the premier tech city for quality
                software development in Africa."
              </p>
              <cite class="text-sm text-accent font-medium">
                - Rob Rickard on stage kicking off the 2017 RWBuild Launch
                Event.
              </cite>
            </blockquote>
          </Motion>

          <Motion
            tag="div"
            class="space-y-8"
            :initial="{ opacity: 0, x: 30 }"
            :animate="{ opacity: 1, x: 0 }"
            :transition="{ duration: 0.8, delay: 0.4 }"
          >
            <div class="rounded-lg overflow-hidden">
              <!-- Image -->
              <img
                :src="rwbuildLaunchImage"
                alt="Rob Rickard presenting at the Rwanda Build Program Launch Event with Hello Kigali app display"
                className="w-full h-auto object-cover"
              />
            </div>
          </Motion>
        </div>
        <!-- Stats -->
        <Motion
          tag="div"
          class="grid grid-cols-2 md:grid-cols-4 gap-4"
          :initial="{ opacity: 0, y: 20 }"
          :animate="{ opacity: 1, y: 0 }"
          :transition="{ duration: 0.8, delay: 0.6 }"
        >
          <div
            v-for="(stat, i) in stats"
            :key="i"
            class="text-center p-4 bg-secondary/50 rounded-lg hover:scale-105 transition duration-200"
          >
            <div class="text-xl font-bold text-accent mb-1">
              {{ stat.value }}
            </div>
            <div class="text-xs text-muted-foreground">{{ stat.label }}</div>
          </div>
        </Motion>
      </Motion>
    </div>
  </section>
</template>

<script setup>
import { useHead } from "@vueuse/head";
// eslint-disable-next-line no-unused-vars
import { ref } from "vue";
import rwbuildLaunchImage from "@/assets/rwbuildLaunchImage.png";
import kigaliSoftwareImage from "@/assets/kigaliSoftwareImage.png";
import { Motion } from "@motionone/vue";
import { Twitter, Linkedin } from "lucide-vue-next";

const stats = [
  { value: "30+", label: "Years Experience" },
  { value: "8+", label: "African Products" },
  { value: "$6M", label: "Funding" },
  { value: "13", label: "Years in Rwanda" },
];

// eslint-disable-next-line no-unused-vars
function onNavigate(page) {
  // This would emit an event if used in parent context
  // $emit('navigate', page)
}

useHead({
  title: "About Rob Rickard",
  meta: [
    {
      name: "description",
      content:
        "About Rob Rickard: US tech entrepreneur, product strategist, and founder of RWBuild and Guhemba, building high-impact systems in Africa.",
    },
    { property: "og:title", content: "About Rob Rickard" },
    {
      property: "og:description",
      content:
        "Rob Rickard is a veteran technology entrepreneur and product strategist based in Kigali, Rwanda.",
    },
    { property: "og:type", content: "profile" },
    { property: "og:url", content: "https://robrickard.com/about" },
    {
      property: "og:image",
      content: "https://robrickard.com/img/robPortrait.65dd6588.png",
    },
  ],
  script: [
    {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Person",
        name: "Rob Rickard",
        description:
          "US tech entrepreneur, product strategist, and founder of RWBuild and Guhemba, building high-impact systems in Africa.",
        image: "https://robrickard.com/img/robPortrait.65dd6588.png",
        alumniOf: {
          "@type": "Organization",
          name: "RWBuild",
        },
        worksFor: {
          "@type": "Organization",
          name: "Guhemba Ltd",
        },
        address: {
          "@type": "PostalAddress",
          addressLocality: "Kigali",
          addressCountry: "Rwanda",
        },
        sameAs: [
          "https://www.linkedin.com/in/robrickard/",
          "https://twitter.com/Rickard",
        ],
      }),
    },
  ],
});
</script>
